import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  PageContent,
  Container,
  Header,
  Row,
  Col,
  Filters,
  Dropdown,
  Heading,
  FiltersControls,
  SeoSection,
  Pagination,
  BoxList,
  Spinner,
} from 'components';
import { connect } from 'react-redux';
import { Padding, Margin } from 'styled-components-spacing';
import {
  loadListCreator,
  setListPageCreator,
  setListPerPageCreator,
  setListSortCreator,
  setListFiltersCreator,
  removeListFiltersCreator,
  loadGlobalEventAttributesCreator,
} from 'store/actionsCreators';
import { checkPermissions, globalPropTypes, isLoading } from 'helpers';
import { useTranslation } from 'react-i18next';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.app.events',
    url: '/events',
  },
];

const EventsList = ({
  appType,
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  loadEventAttributes,
  deliveryModes,
  languages,
  tags,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort } = list;
  const { t } = useTranslation();
  const scrollAfterFiltersRef = useRef(null);

  useEffect(() => {
    loadEventAttributes();
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    setSort('dateFrom.asc');
  }, []);

  const renderHeaderActions = () => {
    switch (appType) {
      case 'ma':
        if (checkPermissions({ roles: ['ma_my_events_create_event'] }, userPermissions)) {
          return ({ Button: ButtonRender }) => (
            <ButtonRender secondary as={Link} to="/ma/event/create">
              {t('page.listEvents.createEventButton')}
            </ButtonRender>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const filtersFields = [
    {
      type: 'input',
      name: 'name',
      id: 'f-name',
      label: t('page.listEvents.nameLabel'),
      placeholder: t('page.listEvents.namePlaceholder'),
    },
    {
      type: 'input',
      name: 'location',
      id: 'f-location',
      label: t('page.listEvents.locationLabel'),
      placeholder: t('page.listEvents.locationPlaceholder'),
    },
    {
      type: 'datepicker',
      name: 'dateFrom',
      id: 'f-dateFrom',
      label: t('page.listEvents.dateFromLabel'),
      placeholder: t('page.listEvents.dateFromPlaceholder'),
    },
    {
      type: 'datepicker',
      name: 'dateTo',
      id: 'f-dateTo',
      label: t('page.listEvents.dateToLabel'),
      placeholder: t('page.listEvents.dateToPlaceholder'),
    },
    {
      type: 'select',
      name: 'type',
      id: 'f-eventType',
      label: t('page.listEvents.eventTypeLabel'),
      placeholder: t('page.listEvents.eventTypePlaceholder'),
      options: [
        {
          label: 'MA',
          value: 'ma',
        },
        {
          label: 'IPMA',
          value: 'ipma',
        },
        {
          label: 'National Young Crew',
          value: 'young_crew',
        },
        {
          label: 'Young Crew Global',
          value: 'young_crew_global',
        },
      ],
    },
    {
      type: 'select',
      name: 'modeOfDelivery',
      id: 'f-modeOfDelivery',
      label: t('page.listEvents.modeOfDeliveryLabel'),
      placeholder: t('page.listEvents.modeOfDeliveryPlaceholder'),
      options: deliveryModes,
    },
    {
      type: 'select',
      name: 'language',
      id: 'f-language',
      label: t('page.listEvents.languageLabel'),
      placeholder: t('page.listEvents.languagePlaceholder'),
      options: languages,
    },
  ];

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={t('page.listEvents.title')}
        renderActions={renderHeaderActions()}
      />
      <PageContent>
        <Container>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Padding all={{ xs: 12, xl: 20 }} className="border-xs">
                <Dropdown
                  openbreakpoint="xl"
                  title={
                    <Heading as="h3" size="h4">
                      {t('page.listEvents.filtersTitle')}
                    </Heading>
                  }
                >
                  <Filters
                    fields={filtersFields}
                    filters={filters}
                    onChangeFilter={(payload) => setFilters(payload)}
                    onClickApplyFilters={() => {
                      setPage(1);
                      loadList();
                    }}
                    onClickRemoveAllFilters={() => removeFilters()}
                    scrollAfterFiltersRef={scrollAfterFiltersRef}
                  />
                </Dropdown>
              </Padding>
            </Col>
            <Margin as={Col} size={{ xl: 2 / 3 }} top={{ xs: 16, xl: 0 }}>
              <FiltersControls
                perPageSelected={perPage}
                perPageOnChange={(payload) => setPerPage(payload.value)}
                sortBySelected={sort}
                sortByOnChange={(payload) => setSort(payload.value)}
                sortByConfig={{
                  name: 'sortBy',
                  options: [
                    { label: 'Date: descending', value: 'createdAt.desc' },
                    { label: 'Date: ascending', value: 'createdAt.asc' },
                    { label: 'Start date: descending', value: 'dateFrom.desc' },
                    { label: 'Start date: ascending', value: 'dateFrom.asc' },
                    { label: 'Name: descending', value: 'name.desc' },
                    { label: 'Name: ascending', value: 'name.asc' },
                  ],
                }}
              />
              {loading ? <Spinner /> : null}
              {!loading ? (
                <div ref={scrollAfterFiltersRef}>
                  <BoxList
                    type="event"
                    items={items}
                    alert={t('global.noResultsFound')}
                    headerAs="h2"
                  />
                  <Pagination
                    styled
                    currentPage={page}
                    totalPages={pages}
                    onChange={(payload) => {
                      setPage(payload);
                      loadList();
                    }}
                  />
                </div>
              ) : null}
            </Margin>
          </Row>
        </Container>
      </PageContent>
      <SeoSection title={t('page.listEvents.seoTitle')}>
        <p className="text-18 font-weight-medium text-gray-200 text-center">
          {t('page.listEvents.seoContent')}
        </p>
      </SeoSection>
    </>
  );
};

EventsList.propTypes = {
  appType: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  loadEventAttributes: PropTypes.func.isRequired,
  deliveryModes: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes),
  languages: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes),
  tags: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes),
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

EventsList.defaultProps = {
  loading: false,
  deliveryModes: [],
  languages: [],
  tags: [],
};

const mapStateToProps = ({
  user,
  loading,
  list: { events: list },
  globals: { event, languages, course },
}) => ({
  appType: user.appType,
  loading: isLoading(loading, 'LOAD_LIST_EVENTS'),
  list,
  deliveryModes: event.deliveryModes,
  languages,
  tags: course.cloudTags,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadListCreator('events')),
  setPage: (payload) => dispatch(setListPageCreator('events', payload)),
  setPerPage: (payload) => dispatch(setListPerPageCreator('events', payload)),
  setSort: (payload) => dispatch(setListSortCreator('events', payload)),
  setFilters: (payload) => dispatch(setListFiltersCreator('events', payload)),
  removeFilters: () => dispatch(removeListFiltersCreator('events')),
  loadEventAttributes: () => dispatch(loadGlobalEventAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventsList);
